import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, MessageCircleHeart, Cake, PartyPopper, Baby,
  Flower2, Gem, HandHeart, Sparkles, HeartPulse, Diamond, HeartCrack,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { emotions } from "../data/siteData";

const icons = {
  Heart, MessageCircleHeart, Cake, PartyPopper, Baby,
  Flower2, Gem, HandHeart, Sparkles, HeartPulse, Diamond, HeartCrack,
};

export default function EmotionSelector() {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Satır kaydırılabilir olduğunda uçlarda solma efekti + ok butonları
  // gösterip kullanıcıya "daha fazla var" sinyali veriyoruz — tek satırlık
  // liste kartın dışına taşınca sessizce kesilmiş gibi görünmesin diye.
  const updateScrollState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  const scrollByPage = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: "smooth" });
  };

  return (
    <section className="py-14 md:py-16 bg-white" data-testid="emotion-selector-section">
      <div className="frida-container">
        <div className="bg-[#222222] rounded-[32px] px-5 py-10 md:px-12 md:py-14 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#D4A15C]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-[#A85C34]/20 blur-3xl pointer-events-none" />

          <h2
            className="relative font-serif text-2xl md:text-3xl lg:text-4xl text-white text-center tracking-tight leading-tight mb-8 md:mb-10"
            data-testid="emotion-selector-title"
          >
            Hangi <span className="italic text-[#D4A15C]">Duyguyu</span> Göndermek İstiyorsunuz?
          </h2>

          <div className="relative">
            {canScrollLeft && (
              <div className="absolute left-0 top-0 bottom-0 w-10 md:w-14 bg-gradient-to-r from-[#222222] to-transparent z-10 pointer-events-none" />
            )}
            {canScrollRight && (
              <div className="absolute right-0 top-0 bottom-0 w-10 md:w-14 bg-gradient-to-l from-[#222222] to-transparent z-10 pointer-events-none" />
            )}

            <div
              ref={scrollerRef}
              className="flex gap-3 md:gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1"
              data-testid="emotion-selector-list"
            >
              {emotions.map((e) => {
                const Icon = icons[e.icon];
                return (
                  <Link
                    key={e.id}
                    to={e.to}
                    className="group shrink-0 snap-start flex flex-col items-center gap-2.5 w-[84px] md:w-[96px]"
                    data-testid={`emotion-${e.id}`}
                  >
                    <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white group-hover:bg-[#D4A15C] group-hover:border-[#D4A15C] group-hover:text-[#222222] group-hover:scale-105 transition-all duration-300">
                      <Icon className="w-6 h-6 md:w-7 md:h-7" />
                    </span>
                    <span className="text-[11px] md:text-xs text-white/75 group-hover:text-white text-center leading-snug transition-colors">
                      {e.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="relative flex items-center justify-center gap-4 mt-8 md:mt-10">
            <button
              onClick={() => scrollByPage(-1)}
              aria-label="Geri kaydır"
              disabled={!canScrollLeft}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors disabled:opacity-20 disabled:pointer-events-none shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <p className="text-center text-white/40 text-xs md:text-sm">
              Duygunuzu seçin, size en uygun çiçekleri sunalım.
            </p>
            <button
              onClick={() => scrollByPage(1)}
              aria-label="İleri kaydır"
              disabled={!canScrollRight}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors disabled:opacity-20 disabled:pointer-events-none shrink-0"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
